import { useEffect, useRef, useState } from 'react';
import { IconSizes, SearchMdIcon } from '@newcore/icons';
import { cn } from '@newcore/ui/cn';
import { FontWeights } from '@newcore/ui/enums/font-weights';
import { TextSizes } from '@newcore/ui/enums/typography-text-sizes';
import { Text } from '@newcore/ui/typography';
import { textVariants } from '@newcore/ui/variants/typography-text-variants';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Command } from 'cmdk';
import { useApplicationLaunch } from '@/hooks/use-application-launch';
import { useApplications } from '@/hooks/use-user-portal';
import { ApplicationSearchItem } from './application-search-item';

interface ApplicationSearchDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ApplicationSearchDialog({
  isOpen,
  onClose,
}: ApplicationSearchDialogProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const { data: applications } = useApplications();

  const { loadingAppId, launchApplication } = useApplicationLaunch({
    onSuccess: onClose,
  });

  useEffect(() => {
    if (!isOpen) {
      // Reset search when the dialog closes.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSearchQuery('');
    }
  }, [isOpen]);

  const allApps = applications?.items ?? [];
  const isTruncated = allApps.length < (applications?.total ?? allApps.length);
  const filteredApps = searchQuery.trim()
    ? allApps.filter(app =>
        app.app_name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : allApps;

  return (
    <DialogPrimitive.Root
      open={isOpen}
      onOpenChange={open => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            'fixed inset-0 z-100 bg-black/25 backdrop-blur-xs',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0'
          )}
        />
        <DialogPrimitive.Content
          className={cn(
            'fixed left-1/2 top-[12vh] z-100 w-[calc(100%-2rem)] max-w-140 -translate-x-1/2 outline-none',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0'
          )}
          onOpenAutoFocus={event => {
            event.preventDefault();
            inputRef.current?.focus();
          }}
          onCloseAutoFocus={event => {
            event.preventDefault();
          }}
        >
          <DialogPrimitive.Title className='sr-only'>
            Search applications
          </DialogPrimitive.Title>
          <DialogPrimitive.Description className='sr-only'>
            Type to search and open your applications
          </DialogPrimitive.Description>

          <Command
            shouldFilter={false}
            className='overflow-hidden rounded-2xl border border-secondary bg-primary shadow-2xl'
            label='Application search'
          >
            <div className='relative flex items-center gap-3 border-b border-secondary px-4'>
              <SearchMdIcon
                size={IconSizes.SM}
                className='shrink-0 text-tertiary'
              />
              <Command.Input
                ref={inputRef}
                value={searchQuery}
                onValueChange={setSearchQuery}
                placeholder='Search applications...'
                className={cn(
                  textVariants({ size: TextSizes.SM }),
                  'h-12 flex-1 bg-transparent outline-none',
                  'placeholder:text-tertiary'
                )}
              />
              <Text
                as='kbd'
                size={TextSizes.XS}
                weight={FontWeights.MEDIUM}
                className='rounded border border-secondary px-1.5 py-0.5 text-tertiary'
              >
                esc
              </Text>
            </div>

            <Command.List className='max-h-[46vh] overflow-y-auto'>
              {filteredApps.length > 0 && (
                <Text
                  as='div'
                  size={TextSizes.XS}
                  className='border-b border-secondary bg-secondary/50 px-4 py-2 text-tertiary'
                >
                  {filteredApps.length}{' '}
                  {filteredApps.length === 1 ? 'result' : 'results'}
                  {isTruncated && ' (some applications may not be searchable)'}
                </Text>
              )}

              {filteredApps.map(app => (
                <ApplicationSearchItem
                  key={app.app_id}
                  app={app}
                  isLoading={loadingAppId === app.app_id}
                  onSelect={launchApplication}
                />
              ))}

              <Command.Empty
                className={cn(
                  textVariants({ size: TextSizes.SM }),
                  'py-8 text-center text-tertiary'
                )}
              >
                No applications found
              </Command.Empty>
            </Command.List>
          </Command>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
